import React, { useEffect } from 'react';
import clsx from 'clsx';
import FavoriteButton from '../../FavoriteButton/FavoriteButton';
import Product from '../../Types/Product';
import { translate } from '../../Services/translation';
import Input from '../Inputs/Input';
import { PRODUCT_DETAIL_LIST_COL_SIZE_LIST } from '../../constants';
import {
  InputId,
  ProductTotal,
  QuickOrderActions,
  TotalType,
  getTotalPrice,
} from '../../Reducers/QuickOrder.reducer';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../Hooks/useTypedSelector';

interface DetailedListBuyFormProps {
  quantity: number;
  product: Product;
  boxesAmount: number;
  rollsAmount: number;
  totalAmount: number;
  packageSize: number;
  setBoxesAmount: (boxesAmount: number | undefined) => void;
  setRollsAmount: (rollsAmount: number | undefined) => void;
  setTotalAmount: (totalAmount: number | undefined) => void;
  setQuantity: (quantity: number) => void;
  isProductPage?: boolean;
}

const QuickOrderDetailedListBuyForm = ({
  isProductPage = false,
  quantity,
  product,
  boxesAmount,
  rollsAmount,
  totalAmount,
  setTotalAmount,
  packageSize,
  setBoxesAmount,
  setRollsAmount,
  setQuantity,
}: DetailedListBuyFormProps) => {
  const dispatch = useDispatch();
  const calculateOnTotal = (value): ProductTotal => {
    let total = value;
    let boxes = boxesAmount ?? 0;
    let rolls = rollsAmount ?? 0;
    let quantity = 0;

    if (value > 0) {
      quantity = value;
      if (value < packageSize) {
        rolls = value;
        boxes = 0;
      } else {
        if (packageSize > 0) {
          boxes = Math.floor(value / packageSize);
          rolls = value % packageSize;
        }
      }
    }
    quantity = value ?? 0;

    return {
      total: total,
      rollsAmount: rolls ?? 0,
      boxesAmount: boxes ?? 0,
      quantity: quantity,
    };
  };

  const calculateOnRolls = (value): ProductTotal => {
    let total = totalAmount ?? 0;
    let boxes = boxesAmount ?? 0;
    let rolls = value ?? 0;
    let quantity = 0;

    if (rolls == 0) {
      total = boxesAmount * packageSize;
    }
    if (rolls > 0) {
      if (!boxesAmount && boxesAmount == 0) {
        if (rolls >= packageSize) {
          boxes = Math.floor(value / packageSize);
          rolls = value % packageSize;
          total = boxes * packageSize + rolls;
        } else {
          total = value;
        }
      } else {
        if (rolls >= packageSize) {
          var boxestoAdd = Math.floor(rolls / packageSize);
          boxes = boxes + boxestoAdd;
          rolls = value % packageSize;
          total = boxes * packageSize + rolls;
        } else {
          total = boxesAmount * packageSize + value;
          boxes = boxesAmount;
        }
      }
    }

    quantity = total;

    return {
      total: total,
      rollsAmount: rolls,
      boxesAmount: boxes,
      quantity: quantity,
    };
  };

  const calculateOnBoxes = (value): ProductTotal => {
    let total = totalAmount ?? 0;
    let boxes = value ?? 0;
    let rolls = rollsAmount ?? 0;
    let quantity = 0;

    if (boxes == 0 && rollsAmount > 0) {
      total = rollsAmount;
    }
    if (boxes > 0) {
      if (rollsAmount == 0) {
        total = boxes * packageSize;
      } else {
        rolls = rollsAmount % packageSize;
        total = boxes * packageSize + rolls;
      }
    }

    quantity = total;

    return {
      total: total,
      rollsAmount: rolls,
      boxesAmount: boxes,
      quantity: quantity,
    };
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id;
    const value = e.target.valueAsNumber;

    if (document.activeElement?.id !== id) return;

    switch (id) {
      case InputId.total:
        const resultTotal = calculateOnTotal(value);
        setTotalAmount(resultTotal.total);
        setRollsAmount(resultTotal.rollsAmount);
        setBoxesAmount(resultTotal.boxesAmount);
        setQuantity(resultTotal.quantity);
        break;
      case InputId.boxes:
        const resultBoxes = calculateOnBoxes(value);
        setTotalAmount(resultBoxes.total);
        setRollsAmount(resultBoxes.rollsAmount);
        setBoxesAmount(resultBoxes.boxesAmount);
        setQuantity(resultBoxes.quantity);
        break;
      case InputId.rolls:
        const resultRolls = calculateOnRolls(value);
        setTotalAmount(resultRolls.total);
        setRollsAmount(resultRolls.rollsAmount);
        setBoxesAmount(resultRolls.boxesAmount);
        setQuantity(resultRolls.quantity);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {packageSize > 1 ? (
        <>
          <div className="flex flex-col self-center">
            <div
              className={clsx(
                'text-md font-bold ',
                !isProductPage && 'lg:hidden'
              )}
            >
              {translate('detailed-product-card.boxes')}
            </div>
            <Input
              id={InputId.boxes}
              value={boxesAmount}
              onChange={(e) => onChange(e)}
              type="number"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  dispatch(QuickOrderActions.setAddNewRow());
                }
              }}
            />
          </div>
          <div className="flex flex-col self-center">
            <div
              className={clsx(
                'text-md font-bold ',
                !isProductPage && 'lg:hidden'
              )}
            >
              {translate('detailed-product-card.rolls')}
            </div>
            <Input
              id={InputId.rolls}
              value={rollsAmount}
              onChange={(e) => onChange(e)}
              type="number"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  dispatch(QuickOrderActions.setAddNewRow());
                }
              }}
            />
          </div>
        </>
      ) : (
        <>
          <div></div>
          <div></div>
        </>
      )}
      <div className="flex flex-col self-center">
        <div
          className={clsx(
            'text-md font-bold ',
            !isProductPage && 'lg:hidden'
          )}
        >
          {translate('detailed-product-card.total')}
        </div>
        <Input
          id={InputId.total}
          value={totalAmount}
          onChange={(e) => onChange(e)}
          type="number"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              dispatch(QuickOrderActions.setAddNewRow());
            }
          }}
        />
      </div>
    </>
  );
};

export default QuickOrderDetailedListBuyForm;
